import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FlexProps,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  ListItem,
  Text,
  UnorderedList,
  useBreakpointValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import { differenceInMinutes, isBefore } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import {
  RiBookmarkLine,
  RiDeleteBinLine,
  RiEditLine,
  RiEyeLine,
  RiLoginCircleLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';
import { useHistory } from 'react-router-dom';
import { CheckinListModal } from '../../../../../../../components/CheckinListModal';
import { ConfirmationModal } from '../../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { BookingPaymentConfirmationModal } from '../../../../../../../components/BookingPaymentConfirmationModal';
import { useAuth } from '../../../../../../../hooks/auth';
import { useBooking } from '../../../../../../../hooks/booking';
import { PrettyBookingPaymentStatus } from '../../../../../../../models/bookings';
import cancelBookingsService from '../../../../../../../services/Bookings/CancelBookingsService';
import { createBookingExternalPaymentsService } from '../../../../../../../services/Bookings/CreateBookingExternalPaymentsService';
import { translateError } from '../../../../../../../utils/errors';
import { IHandleBookingPayment } from '../../../../Activities/ActivityDetails/components/ActivityDetailsData/components/ActivityDetailsTabs/components/ActivityCalendar/components/ScheduleBookingsModal/components/ScheduleTabs/components/ScheduleBookingsTable';
import BookingCancellationInfo from './components/BookingCancellationInfo';
import BookingDetailsUserInfo from './components/BookingDetailsUserInfo';
import { BookingUpdateModal } from './components/BookingUpdateModal';

export const BookingDetailsInfo = (props: FlexProps): JSX.Element => {
  const toast = useToast();

  const { push } = useHistory();

  const { assignment: authenticatedAssignment, experienceLevelsTitleMap } =
    useAuth();

  const { booking, handleBooking } = useBooking();

  const dividerOrientation = useBreakpointValue({
    base: 'horizontal',
    xl: 'vertical',
  });

  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isCheckinListModalVisible, setIsCheckinListModalVisible] =
    useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [isBookingUpdateModalVisible, setIsBookingUpdateModalVisible] =
    useState(false);

  const togglePaymentModal = useCallback(() => {
    setIsPaymentModalVisible((prevState) => !prevState);
  }, []);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleToggleCheckinListModal = useCallback(() => {
    setIsCheckinListModalVisible((prevState) => !prevState);
  }, []);

  const { isPaymentButtonDisabled, paymentButtonColorScheme } = useMemo(() => {
    const isDisabled =
      !booking?.payment ||
      !['WAITING', 'FAILED', 'REJECTED', 'PAYMENT_EXTERNAL_PENDING'].includes(
        booking.payment.status,
      ) ||
      !authenticatedAssignment?.featureGroup.features.some((feature) =>
        [
          'BOOKING_PAYMENT_FULL_ACCESS',
          'BOOKING_PAYMENT_WRITE_ACCESS',
        ].includes(feature.key),
      );

    let colorScheme: string = isDisabled ? 'green' : 'red';

    if (booking?.payment?.status === 'PAYMENT_EXTERNAL_PENDING') {
      colorScheme = 'yellow';
    }

    return {
      isPaymentButtonDisabled: isDisabled,
      paymentButtonColorScheme: colorScheme,
    };
  }, [authenticatedAssignment?.featureGroup.features, booking?.payment]);

  let cancelBookingWarningMessage: string | undefined;

  const bookedDateTime = useMemo(
    () =>
      new Date(
        booking?.bookedDate.concat(`T${booking.activitySchedule.startTime}`) ||
          '',
      ),
    [booking?.activitySchedule.startTime, booking?.bookedDate],
  );

  const bookedTimePast = isBefore(bookedDateTime, new Date());

  const denyCancelHistories: string[] = [];

  if (booking?.checkins.length) {
    denyCancelHistories.push('checkin concluído');
  }

  if (bookedTimePast) {
    denyCancelHistories.push('evento já iniciou');
  }

  let isCancelable = !booking?.checkins.length && !bookedTimePast;

  if (
    booking?.payment &&
    booking.user.userAssignments[0]?.featureGroup.key.includes('GUEST') &&
    ['PROCESSING', 'ONGOING', 'APPROVED', 'PENDING', 'VALIDATING'].includes(
      booking.payment.status,
    )
  ) {
    const guestScheduleRefundMinutesLimit = 60 * 24; // minutes * hours

    const minutesToBookedScheduleInit = differenceInMinutes(
      bookedDateTime,
      new Date(),
    );

    isCancelable =
      minutesToBookedScheduleInit > guestScheduleRefundMinutesLimit;

    if (!isCancelable) {
      denyCancelHistories.push(
        'reservas de convidados, com pagamento, só podem ser canceladas até 24 horas antes do início do evento',
      );
    }
  }

  if (booking?.payment?.status === 'PAYMENT_EXTERNAL') {
    cancelBookingWarningMessage =
      'Essa reserva foi paga no clube, o reembolso também deverá ser feito externamente.';
  }

  const handleBookingPayment = useCallback(
    async ({ bookingId, payment }: IHandleBookingPayment) => {
      try {
        const paidBooking = await createBookingExternalPaymentsService({
          bookingId,
          payment,
        });

        handleBooking(paidBooking);

        togglePaymentModal();

        toast({
          title: 'Transação concluída com sucesso',
          description: 'A transação foi efetuada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'user-address-missing') {
            toast({
              title: 'Cadastro incompleto',
              description: `Para registrar o pagamento será necessário completar o cadastro do membro. ${translateError(
                { message: err.response?.data.message },
              )}`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            return;
          }

          toast({
            title: 'Falha na transação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao efetuar a transação, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [handleBooking, togglePaymentModal, toast],
  );

  const deleteBooking = useCallback(
    async (bookingId: string, reason?: string) => {
      try {
        await cancelBookingsService({ bookingId, reason });

        toast({
          title: 'Cancelada com sucesso',
          description: 'A reserva foi cancelada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/bookings');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao cancelar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cancelar a reserva, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [push, toast],
  );

  return (
    <>
      {!!booking &&
        authenticatedAssignment?.featureGroup.features.some((feature) =>
          [
            'BOOKING_CHECKIN_FULL_ACCESS',
            'BOOKING_CHECKIN_READ_ACCESS',
            'BOOKING_CHECKIN_WRITE_ACCESS',
          ].includes(feature.key),
        ) && (
          <CheckinListModal
            bookingId={booking.id}
            checkinEnabled={
              !booking.deletedAt &&
              (!booking.payment ||
                [
                  'APPROVED',
                  'PAYMENT_EXTERNAL_PENDING',
                  'PAYMENT_EXTERNAL',
                  'PAYMENT_HOTEL',
                ].includes(booking.payment.status))
            }
            isOpen={isCheckinListModalVisible}
            onClose={handleToggleCheckinListModal}
          />
        )}

      {!!booking?.payment?.status && (
        <BookingPaymentConfirmationModal
          isOpen={isPaymentModalVisible}
          onClose={togglePaymentModal}
          onConfirm={(payment) =>
            handleBookingPayment({
              bookingId: booking.id,
              payment,
            })
          }
          payAfterDisabled={
            booking.payment.status === 'PAYMENT_EXTERNAL_PENDING'
          }
          user={booking.user}
        />
      )}

      {!!booking && (
        <BookingUpdateModal
          booking={booking}
          isOpen={isBookingUpdateModalVisible}
          onClose={() => {
            setIsBookingUpdateModalVisible((prevState) => !prevState);
          }}
          onConfirm={handleBooking}
        />
      )}

      {!!booking && (
        <ConfirmationModal
          isOpen={isDeleteConfirmationModalVisible}
          onClose={handleToggleDeleteConfirmationModal}
          onConfirm={(reason) => deleteBooking(booking.id, reason)}
          title="Confirmar cancelamento"
          message="Deseja realmente cancelar?"
          reasonAlert="Motivo requerido, ultrapassou tempo mínimo para cancelamento antes do início do evento"
          isReasonRequired={booking.exceededCancellationDeadline}
          showReasonInput
          warningMessage={cancelBookingWarningMessage}
        />
      )}

      <Flex
        direction="column"
        w="100%"
        borderRadius={8}
        bg="white"
        p="8"
        {...props}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Detalhes da reserva
          </Heading>
        </Flex>

        {booking && (
          <Flex direction="column" mt="8">
            <Grid
              templateColumns={[
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(12, 1fr)',
              ]}
              gap="4"
              width="100%"
            >
              <GridItem colSpan={[12, 12, 12, 12, 8]}>
                <Flex direction="column" px="8">
                  <VStack flex="1" alignItems="flex-start" pl="8">
                    <Text fontSize="4xl" fontWeight="bold">
                      {booking.activitySchedule.activity.name}
                    </Text>

                    <HStack>
                      <Text>Local:</Text>
                      <Text color="gray.500">
                        {booking.activitySchedule.activity.spot.name}
                      </Text>
                    </HStack>

                    <HStack>
                      <Text>Horário reservado:</Text>
                      <Text color="gray.500">{`${booking.formattedBookedDate} ${booking.formattedStartTime} - ${booking.formattedEndTime}`}</Text>
                    </HStack>

                    <HStack>
                      <Text>Nível reservado:</Text>
                      <Text color="gray.500">
                        {experienceLevelsTitleMap.get(
                          booking.activitySchedule.minUserExperience,
                        )}
                      </Text>
                    </HStack>

                    <HStack>
                      <Text>Reservado em:</Text>
                      <Text color="gray.500">{booking.formattedCreatedAt}</Text>
                    </HStack>

                    {!!booking.bookingPrice && (
                      <>
                        <HStack>
                          <Text>Valor da reserva:</Text>
                          <Text color="gray.500">
                            {booking.formattedBookingPrice}
                          </Text>
                        </HStack>

                        <HStack>
                          <Text>Valor dos opcionais:</Text>
                          <Text color="gray.500">
                            {booking.formattedItemsPrice}
                          </Text>
                        </HStack>

                        <HStack>
                          <Text>Valor total:</Text>
                          <Text color="gray.500">
                            {booking.formattedTotalPrice}
                          </Text>
                        </HStack>

                        {!!booking.payment && (
                          <>
                            <HStack>
                              <Text>Status do pagamento:</Text>
                              <Text>
                                {
                                  PrettyBookingPaymentStatus[
                                    booking.payment.status
                                  ]
                                }
                              </Text>
                            </HStack>

                            <HStack>
                              <Text>
                                Data da última alteração no pagamento:
                              </Text>
                              <Text>{booking.payment.formattedUpdatedAt}</Text>
                            </HStack>
                          </>
                        )}
                      </>
                    )}
                  </VStack>

                  <ButtonGroup mt="8" pl="8">
                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'ACTIVITY_FULL_ACCESS',
                          'ACTIVITY_READ_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <LinkButton
                        icon={<Icon as={RiEyeLine} fontSize="20" />}
                        to={{
                          pathname: '/activities/details',
                          state: {
                            activityId: booking.activitySchedule.activityId,
                          },
                        }}
                      >
                        Atividade
                      </LinkButton>
                    )}

                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        ['SPOT_FULL_ACCESS', 'SPOT_READ_ACCESS'].includes(
                          feature.key,
                        ),
                    ) && (
                      <LinkButton
                        icon={<Icon as={RiEyeLine} fontSize="20" />}
                        to={{
                          pathname: '/spots/details',
                          state: {
                            spotId: booking.activitySchedule.activity.spotId,
                          },
                        }}
                      >
                        Local
                      </LinkButton>
                    )}

                    {(!booking.payment ||
                      [
                        'APPROVED',
                        'PAYMENT_EXTERNAL_PENDING',
                        'PAYMENT_EXTERNAL',
                        'PAYMENT_HOTEL',
                      ].includes(booking.payment.status)) &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'BOOKING_CHECKIN_FULL_ACCESS',
                            'BOOKING_CHECKIN_READ_ACCESS',
                            'BOOKING_CHECKIN_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <Button
                          size="sm"
                          fontSize="sm"
                          colorScheme="green"
                          onClick={handleToggleCheckinListModal}
                          leftIcon={
                            <Icon as={RiLoginCircleLine} fontSize="20" />
                          }
                        >
                          Checkins
                        </Button>
                      )}

                    {!booking.deletedAt &&
                      !!booking.payment &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'BOOKING_PAYMENT_FULL_ACCESS',
                            'BOOKING_PAYMENT_WRITE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <Button
                          size="sm"
                          fontSize="sm"
                          colorScheme={paymentButtonColorScheme}
                          color="white"
                          disabled={isPaymentButtonDisabled}
                          onClick={togglePaymentModal}
                          leftIcon={
                            <Icon as={RiMoneyDollarCircleLine} fontSize="20" />
                          }
                        >
                          Pagar no clube
                        </Button>
                      )}

                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'BOOKING_FULL_ACCESS',
                          'BOOKING_WRITE_ACCESS',
                        ].includes(feature.key),
                    ) &&
                      !booking.deletedAt &&
                      !booking.isExpired && (
                        <Button
                          size="sm"
                          fontSize="sm"
                          color="white"
                          colorScheme="yellow"
                          leftIcon={<Icon as={RiEditLine} fontSize="16" />}
                          onClick={() =>
                            setIsBookingUpdateModalVisible(
                              (prevState) => !prevState,
                            )
                          }
                        >
                          Editar
                        </Button>
                      )}

                    {!booking?.deletedAt &&
                      isCancelable &&
                      authenticatedAssignment?.featureGroup.features.some(
                        (feature) =>
                          [
                            'BOOKING_FULL_ACCESS',
                            'BOOKING_DELETE_ACCESS',
                          ].includes(feature.key),
                      ) && (
                        <Button
                          size="sm"
                          fontSize="sm"
                          colorScheme="red"
                          onClick={handleToggleDeleteConfirmationModal}
                          leftIcon={<Icon as={RiDeleteBinLine} fontSize="20" />}
                        >
                          Cancelar
                        </Button>
                      )}
                  </ButtonGroup>

                  {!booking.deletedAt && !isCancelable && (
                    <Box mt="4" pl="8">
                      <Text fontSize="sm" color="red.500">
                        Essa reserva não pode ser cancelada:
                      </Text>

                      <UnorderedList mt="2">
                        {denyCancelHistories.map((message) => (
                          <ListItem key={message} fontSize="sm" color="red.500">
                            <Text fontSize="sm" color="red.500">
                              {message}
                            </Text>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    </Box>
                  )}
                </Flex>
              </GridItem>

              {booking.description && (
                <GridItem colSpan={[12, 12, 12, 12, 4]}>
                  <Flex height="100%">
                    <Divider
                      hidden={dividerOrientation === 'horizontal'}
                      px="2"
                      height="80%"
                      borderColor="gray.300"
                      orientation="vertical"
                    />

                    <Flex flexDirection="column" w="100%">
                      <Divider
                        hidden={dividerOrientation === 'vertical'}
                        borderColor="gray.300"
                        mt="4"
                        mb="8"
                      />
                      <Flex
                        flexDirection="column"
                        px={[8, 8, 8, 8, 0]}
                        w="100%"
                      >
                        <HStack mt={[0, 0, 0, 0, 2]}>
                          <Icon as={RiBookmarkLine} fontSize="20" />
                          <Text>Biografia</Text>
                        </HStack>

                        <Text
                          fontSize="sm"
                          color="gray.500"
                          mt="4"
                          px={[4, 4, 4, 4, 0]}
                        >
                          {booking.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </GridItem>
              )}
            </Grid>

            <Divider borderColor="gray.300" my="8" />

            <BookingDetailsUserInfo user={booking.user} />

            {!!booking.cancellationHistory && (
              <>
                <Divider borderColor="gray.300" my="8" />

                <BookingCancellationInfo
                  cancellationHistory={booking.cancellationHistory}
                />
              </>
            )}
          </Flex>
        )}
      </Flex>
    </>
  );
};
